import { TimesheetReportType } from 'containers/timesheets/utils/TimesheetReportType';
import { usePermissions } from 'hooks';
import { usePtoUnpaidHours } from 'hooks/feature/time-off/usePtoUnpaidHours/usePtoUnpaidHours';
import useEmployeeNameFormatter from 'hooks/ui/useEmployeeNameFormatter';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import ITimeOff from 'types/TimeOff';
import { dateTimeFromISOKeepZone, getDateString } from 'utils/dateUtils';
import { t } from 'utils/localize';
import { getTotalAsHoursMinutesSeconds } from 'utils/stringUtils';
import { ITimeEntryDataTableRow } from '../TimeEntryDataTable';

export function useTimeOffToTimeOffRowMap() {
  const { hasPermissionToManage } = usePermissions();
  const enablePtoUnpaidHours = usePtoUnpaidHours();

  const employeeNameFormatter = useEmployeeNameFormatter();

  return useCallback(
    (timeOff: ITimeOff, shouldIncludeUnpaidPtoInTotal?: boolean): ITimeEntryDataTableRow => {
      const dateStamp = dateTimeFromISOKeepZone(timeOff.dateStamp);

      const includeSeconds = timeOff.paid || enablePtoUnpaidHours;
      const seconds = includeSeconds && timeOff.seconds ? timeOff.seconds : 0;

      const shouldIncludeInTotal = shouldIncludeUnpaidPtoInTotal || timeOff.paid;
      const totalSeconds = shouldIncludeInTotal ? timeOff.seconds || 0 : 0;

      return {
        id: timeOff.id,
        key: timeOff.id + getDateString(dateStamp, 'ccc, LLL d', true),
        startDate: dateStamp,
        endDate: dateStamp,
        member: timeOff.member,
        date: getDateString(dateStamp, 'ccc, LLL d', true),
        day: dateStamp,
        employee: employeeNameFormatter(timeOff.member.firstName ?? '', timeOff.member.lastName ?? ''),
        type: timeOff.paid ? t('PTO') : t('Unpaid'),
        total: getTotalAsHoursMinutesSeconds(seconds),
        totalSeconds,
        start: '---',
        startSup: null,
        end: '---',
        endSup: null,
        description: timeOff.description,
        timeOffType: timeOff.type,
        actions: null,
        injured: '---',
        breakCompliance: '---',
        timeAccurate: '---',
        ot: '0:00',
        otSeconds: 0,
        dt: '0:00',
        dtSeconds: 0,
        // Metadata not shown in table
        timesheetReportType: timeOff.paid ? TimesheetReportType.TIME_OFF_PAID : TimesheetReportType.TIME_OFF_UNPAID,
        canManage: timeOff.member ? hasPermissionToManage(timeOff.member, 'manageTimeOff') : false,
        isArchived: !isNil(timeOff.member.archivedOn),
        exportStartISO: 'dont care',
        exportEndISO: 'dont care',
      };
    },
    [employeeNameFormatter, hasPermissionToManage, enablePtoUnpaidHours]
  );
}
